import { isText, args2str } from './bits'

import envRaw from './env-raw'
import debugRaw from './debug-raw'
import StrictObj from './strict-obj'

let id = 0
const nextId = () => id++

export default function Emitter(rawName) {
  let preList = []
  let mainList = []
  let afterList = []

  let isPreEmitRunning = false
  let isMainEmitRunning = false
  let isAfterEmitRunning = false

  let counter = 0
  let listenersLimit = 50

  const hasName = isText(rawName)
  const name = hasName ? rawName : null

  const emitter = StrictObj({
    id: nextId(),
    name,
    on,
    pre,
    off,
    has,
    emit,
    size,
    clear,
    after,
    count,
    getPreList,
    getMainList,
    getAfterList,
    setMaxListeners,
    getMaxListeners
  })

  return emitter

  function getPreList() {
    return preList.concat()
  }

  function getMainList() {
    return mainList.concat()
  }

  function getAfterList() {
    return afterList.concat()
  }

  function getMaxListeners() {
    return listenersLimit
  }

  function setMaxListeners(limit) {
    listenersLimit = limit
    return emitter
  }

  function emit() {
    counter++

    // pre
    if (envRaw.showsEmitterDebug && hasName && preList.length) {
      console.debug(`${Date.now()} !${emitter.name} emitter with id=${emitter.id} calls ${preList.length} pre-subscribers for the ${counter}-th time!`, args2str(arguments))
    }

    const lastIsPreEmitRunning = isPreEmitRunning
    const localPreList = preList
    isPreEmitRunning = true

    for (let index = 0, length = localPreList.length; index !== length; index++) {
      const preSub = localPreList[index]
      preSub.apply(null, arguments)
    }

    isPreEmitRunning = lastIsPreEmitRunning

    // main
    if (envRaw.showsEmitterDebug && hasName && mainList.length) {
      console.debug(`${Date.now()} !${emitter.name} emitter with id=${emitter.id} calls ${mainList.length} main-subscribers for the ${counter}-th time!`, args2str(arguments))
    }

    const lastIsMainEmitRunning = isMainEmitRunning
    const localMainList = mainList
    isMainEmitRunning = true

    for (let index = 0, length = localMainList.length; index !== length; index++) {
      const mainSub = localMainList[index]
      mainSub.apply(null, arguments)
    }

    isMainEmitRunning = lastIsMainEmitRunning

    // after
    if (envRaw.showsEmitterDebug && hasName && afterList.length) {
      console.debug(`${Date.now()} !${emitter.name} emitter with id=${emitter.id} calls ${afterList.length} after-subscribers for the ${counter}-th time!`, args2str(arguments))
    }

    const lastIsAfterEmitRunning = isAfterEmitRunning
    const localAfterList = afterList
    isAfterEmitRunning = true

    for (let index = 0, length = localAfterList.length; index !== length; index++) {
      const afterSub = localAfterList[index]
      afterSub.apply(null, arguments)
    }

    isAfterEmitRunning = lastIsAfterEmitRunning

    // done
    return emitter
  }

  function pre(sub) {
    if (has(sub)) {
      return emitter
    }
    if (envRaw.devMode && preList.length > listenersLimit) {
      debugRaw(`emitter[id=${emitter.id}]`, emitter)
      console.warn(`possible Emitter#${emitter.id} memory leak detected. ${preList.length} pre-subscribers added!`)
    }
    if (isPreEmitRunning) {
      preList = preList.concat()
    }
    preList.push(sub)
    return emitter
  }

  function on(sub) {
    if (has(sub)) {
      return emitter
    }
    if (envRaw.devMode && mainList.length > listenersLimit) {
      debugRaw(`emitter[id=${emitter.id}]`, emitter)
      console.warn(`possible Emitter#${emitter.id} memory leak detected. ${mainList.length} main-subscribers added!`)
    }
    if (isMainEmitRunning) {
      mainList = mainList.concat()
    }
    mainList.push(sub)
    return emitter
  }

  function after(sub) {
    if (has(sub)) {
      return emitter
    }
    if (envRaw.devMode && afterList.length > listenersLimit) {
      debugRaw(`emitter[id=${emitter.id}]`, emitter)
      console.warn(`possible Emitter#${emitter.id} memory leak detected. ${afterList.length} after-subscribers added!`)
    }
    if (isAfterEmitRunning) {
      afterList = afterList.concat()
    }
    afterList.push(sub)
    return emitter
  }

  function off(sub) {
    const preIndex = preList.indexOf(sub)
    if (preIndex !== -1) {
      if (isPreEmitRunning) {
        preList = preList.concat()
      }
      preList.splice(preIndex, 1)
    }

    const mainIndex = mainList.indexOf(sub)
    if (mainIndex !== -1) {
      if (isMainEmitRunning) {
        mainList = mainList.concat()
      }
      mainList.splice(mainIndex, 1)
    }

    const afterIndex = afterList.indexOf(sub)
    if (afterIndex !== -1) {
      if (isAfterEmitRunning) {
        afterList = afterList.concat()
      }
      afterList.splice(afterIndex, 1)
    }

    return emitter
  }

  function has(sub) {
    return false
      || preList.indexOf(sub) !== -1
      || mainList.indexOf(sub) !== -1
      || afterList.indexOf(sub) !== -1
  }

  function size() {
    return preList.length + mainList.length + afterList.length
  }

  function clear() {
    if (isPreEmitRunning) {
      preList = preList.concat()
    }
    preList.length = 0

    if (isMainEmitRunning) {
      mainList = mainList.concat()
    }
    mainList.length = 0

    if (isAfterEmitRunning) {
      afterList = afterList.concat()
    }
    afterList.length = 0
    return emitter
  }

  function count() {
    return counter
  }
}
